import ArrowCornerRightIcon from '@assets/icons/arrow-corner-right.svg';
import EmployeesIcon from '@assets/icons/employees.svg';
import i18n from '@/utils/i18n';
import NoteIcon from '@assets/icons/note.svg';
import PencilIcon from '@assets/icons/pencil.svg';
import React, { useMemo } from 'react';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from 'react-native';
import { OnlineMenuModal } from '@/experiences/sales-portal/containers/create-order-guide/online-menu-modal';
import { ProspectDetailLayout } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/components/prospect-detail-layout';
import { QuickActionItem } from '@/experiences/sales-portal/components/quick-actions-card';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { UploadPhotoOrderGuideModalContainer } from '@/experiences/sales-portal/containers/create-order-guide/upload-photo-order-guide-modal-container';
import { useActivityNotesLoader } from '@/experiences/sales-portal/blocs/loaders/use-activity-notes-loader';
import {
  useAlertContext,
  usePageLayoutBodyScroll,
  usePageLayoutHeaderInset,
} from '@usepepper/sprout-kit-ui';
import { useBusinessPhoneNumberLoader } from '@/experiences/sales-portal/blocs/loaders/use-business-phone-number-loader';
import { useCreateOrderGuideQuickAction } from '@/experiences/sales-portal/containers/create-order-guide/blocs/hooks/use-create-order-guide-quick-action';
import { useDeactivateProspectMutator } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/mutators/use-deactivate-prospect-mutator';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useIsMobileBottomNavLayout } from '@sales-portal/blocs/hooks/use-is-mobile-bottom-nav-layout';
import { useNavigation } from '@react-navigation/native';
import { useProspectDetailLoader } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/loaders/use-prospect-detail-loader';
import { useProspectStageLoader } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/loaders/use-prospect-stage-loader';
import { useUpdateProspectStageMutator } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/mutators/use-update-prospect-stage-mutator';
import { validate, version } from 'uuid';

const UUID_VERSION_4 = 4;

const NEAR_END_THRESHOLD_PX = 50;
const DESKTOP_SCROLL_EVENT_THROTTLE_MS = 100;
const MOBILE_SCROLL_EVENT_THROTTLE_MS = 16;
const MOBILE_BOTTOM_NAV_CLEARANCE_PX = 96;

interface ProspectDetailContentProps {
  onAddActivityNote: () => void;
  onEditProspect: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

export const ProspectDetailContent: React.FC<ProspectDetailContentProps> = ({
  onAddActivityNote,
  onEditProspect,
  restaurantUUID,
  supplierUUID,
}) => {
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();
  const [styles] = useStyles();
  const isMobileBottomNavLayout = useIsMobileBottomNavLayout();
  const { onScroll: reportBodyScroll } = usePageLayoutBodyScroll();
  const headerInset = usePageLayoutHeaderInset();
  const { switchToExperience } = useExperienceStore();
  const { alert } = useAlertContext('prospect_detail_mark_lost');
  const { showErrorToast, showToast } = useExperienceToasts();

  const areUUIDsValid =
    validate(restaurantUUID) &&
    validate(supplierUUID) &&
    version(restaurantUUID) === UUID_VERSION_4 &&
    version(supplierUUID) === UUID_VERSION_4;

  const {
    accountId,
    address,
    chatUUID,
    coordinate,
    error,
    loading,
    refetch,
    restaurant,
  } = useProspectDetailLoader({
    restaurantUUID,
    skip: !areUUIDsValid,
    supplierUUID,
  });

  const {
    loading: phoneNumberLoading,
    phoneNumber,
    refetch: refetchPhoneNumber,
  } = useBusinessPhoneNumberLoader({
    restaurantUUID,
    skip: !areUUIDsValid,
    supplierUUID,
  });

  const {
    activityNotes,
    error: activityNotesError,
    fetchMore: fetchMoreActivityNotes,
    fetchingMore: fetchingMoreActivityNotes,
    loading: loadingActivityNotes,
    refetch: refetchActivityNotes,
  } = useActivityNotesLoader({
    restaurantUUID,
    skip: !areUUIDsValid,
    supplierUUID,
  });

  const {
    closeOnlineMenuModal,
    closeUploadPhotoModal,
    isOnlineMenuModalOpen,
    isUploadPhotoModalOpen,
    loading: createOrderGuideLoading,
    quickAction: createOrderGuideAction,
  } = useCreateOrderGuideQuickAction({
    chatUUID,
    label: i18n.t(
      'sales_portal.prospect_detail.quick_actions.create_order_guide',
    ),
    supplierUUID,
  });

  const quickActionsLoading = loading || createOrderGuideLoading;

  const {
    error: stageError,
    loading: stageLoading,
    refetch: refetchStage,
    stage: loadedStage,
  } = useProspectStageLoader({
    restaurantUUID,
    skip: !areUUIDsValid,
    supplierUUID,
  });
  const { selectedStage, selectStage } = useUpdateProspectStageMutator({
    loadedStage,
    restaurantUUID,
    supplierUUID,
  });

  const { deactivateProspect } = useDeactivateProspectMutator({
    restaurantUUID,
    supplierUUID,
  });

  const stage = selectedStage ?? loadedStage;
  const restaurantName = restaurant?.name;

  const markers = useMemo(
    () =>
      coordinate && restaurantName
        ? [
            {
              accessibilityLabel: i18n.t(
                'sales_portal.prospect_detail.map_marker_label',
                { restaurantName },
              ),
              coordinate,
              label: restaurantName,
            },
          ]
        : undefined,
    [coordinate, restaurantName],
  );

  const quickActions: QuickActionItem[] = [
    ...(createOrderGuideAction ? [createOrderGuideAction] : []),
    ...(chatUUID
      ? [
          {
            Icon: NoteIcon,
            label: i18n.t(
              'sales_portal.prospect_detail.quick_actions.add_activity_note',
            ),
            onPress: onAddActivityNote,
          },
        ]
      : []),
    {
      Icon: EmployeesIcon,
      label: i18n.t(
        'sales_portal.prospect_detail.quick_actions.employee_management',
      ),
      onPress: () => {
        navigation.push(SALES_PORTAL_ROUTES.ProspectEmployeeList, {
          restaurantUUID,
          supplierUUID,
        });
      },
    },
    {
      Icon: PencilIcon,
      label: i18n.t('sales_portal.prospect_detail.quick_actions.edit_customer'),
      onPress: onEditProspect,
    },
    {
      Icon: ArrowCornerRightIcon,
      label: i18n.t(
        'sales_portal.prospect_detail.quick_actions.view_in_operator',
      ),
      onPress: () => {
        switchToExperience(ExperienceMode.OPERATOR_PORTAL);
      },
    },
  ];

  const handleBackToProspects = () => {
    navigation.navigate(SALES_PORTAL_ROUTES.Prospects, { supplierUUID });
  };

  const handleSelectLost = () => {
    const prospectName = restaurantName ?? '';
    alert({
      buttons: [
        {
          action: () => {},
          style: 'cancel',
          title: i18n.t('cancel'),
        },
        {
          action: async () => {
            try {
              await deactivateProspect();
              showToast({
                title: i18n.t(
                  'sales_portal.prospect_detail.mark_lost.success',
                  { prospectName },
                ),
                variant: 'success',
              });
              handleBackToProspects();
            } catch (deactivateError) {
              captureException(deactivateError);
              showErrorToast(
                i18n.t('sales_portal.prospect_detail.mark_lost.error'),
              );
            }
          },
          style: 'destructive',
          title: i18n.t('sales_portal.prospect_detail.mark_lost.confirm'),
        },
      ],
      message: i18n.t('sales_portal.prospect_detail.mark_lost.message', {
        prospectName,
      }),
      title: i18n.t('sales_portal.prospect_detail.mark_lost.title', {
        prospectName,
      }),
    });
  };

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    reportBodyScroll(event);
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const distanceFromBottom =
      contentSize.height - (layoutMeasurement.height + contentOffset.y);
    if (distanceFromBottom <= NEAR_END_THRESHOLD_PX) {
      fetchMoreActivityNotes();
    }
  };

  const handleRetry = () => {
    refetch();
    refetchPhoneNumber();
    refetchStage();
  };

  return (
    <>
      <ScrollView
        accessibilityLabel="prospect-detail-scroll"
        contentContainerStyle={[
          styles.scrollContent,
          { paddingTop: headerInset },
          isMobileBottomNavLayout ? styles.mobileScrollContent : null,
        ]}
        onScroll={handleScroll}
        scrollEventThrottle={
          isMobileBottomNavLayout
            ? MOBILE_SCROLL_EVENT_THROTTLE_MS
            : DESKTOP_SCROLL_EVENT_THROTTLE_MS
        }
        showsVerticalScrollIndicator={false}
        style={styles.scroll}
      >
        <ProspectDetailLayout
          accountId={accountId}
          activityNotes={activityNotes}
          activityNotesHasError={Boolean(activityNotesError)}
          address={address}
          coordinate={coordinate}
          fetchingMoreActivityNotes={fetchingMoreActivityNotes}
          hasError={Boolean(error) || Boolean(stageError)}
          isPhoneNumberLoading={phoneNumberLoading}
          isStageLoading={stageLoading}
          loading={loading}
          loadingActivityNotes={loadingActivityNotes}
          markers={markers}
          onBackToProspects={handleBackToProspects}
          onRetry={handleRetry}
          onRetryActivityNotes={() => refetchActivityNotes()}
          onSelectLost={handleSelectLost}
          onSelectStage={selectStage}
          phoneNumber={phoneNumber}
          quickActions={quickActions}
          quickActionsLoading={quickActionsLoading}
          restaurantName={restaurantName}
          stage={stage}
        />
      </ScrollView>
      <OnlineMenuModal
        isOpen={isOnlineMenuModalOpen}
        onClose={closeOnlineMenuModal}
        restaurantName={restaurantName}
        restaurantUUID={restaurantUUID}
        supplierUUID={supplierUUID}
      />
      <UploadPhotoOrderGuideModalContainer
        isOpen={isUploadPhotoModalOpen}
        onClose={closeUploadPhotoModal}
        restaurantName={restaurantName}
        restaurantUUID={restaurantUUID}
        supplierUUID={supplierUUID}
      />
    </>
  );
};

const useStyles = createStyles(() => ({
  mobileScrollContent: {
    paddingBottom: MOBILE_BOTTOM_NAV_CLEARANCE_PX,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
}));
